import { ConflictMarker } from '../Timeline.types';

/**
 * Converts a canonical ISO date string (YYYY-MM-DD) to an integer day number (days since the epoch).
 * `Date.UTC` handles month/year boundaries, so adjacency checks reduce to integer comparison.
 */
function isoToDayNumber(isoDate: string): number {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    const day = Number(isoDate.slice(8, 10));
    return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * Given the set of days that have a conflict (ISO date strings, YYYY-MM-DD), returns a map assigning each
 * day its Start/Middle/End/Single marker shape based on whether the adjacent calendar days also conflict.
 * Contiguous days always form a single group (a lone Single is never adjacent to a Start/Middle/End).
 *
 * Used for aggregated (non-leaf) rows, where the conflicting days are the union across several locations.
 */
export function recalculateConflictMarkerShapes(conflictDates: Set<string>): Map<string, ConflictMarker> {
    const result = new Map<string, ConflictMarker>();

    // Convert each ISO date to an integer day number once, then decide adjacency by set membership.
    const dayNumbers = new Set<number>();
    const entries: Array<{ isoDate: string; dayNumber: number }> = [];
    for (const isoDate of conflictDates) {
        const dayNumber = isoToDayNumber(isoDate);
        dayNumbers.add(dayNumber);
        entries.push({ isoDate, dayNumber });
    }

    for (const { isoDate, dayNumber } of entries) {
        const hasPrevious = dayNumbers.has(dayNumber - 1);
        const hasNext = dayNumbers.has(dayNumber + 1);

        let marker: ConflictMarker;
        if (hasPrevious && hasNext) {
            marker = 'Conflict-Middle';
        } else if (hasNext) {
            marker = 'Conflict-Start';
        } else if (hasPrevious) {
            marker = 'Conflict-End';
        } else {
            marker = 'Conflict-Single';
        }

        result.set(isoDate, marker);
    }

    return result;
}
